import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useGetOpeningStoreStockDetailsQuery, useCreateOpeningStoreStockMutation } from '../slices/openingStoreStocksApiSlice';
import { useGetSuppliersQuery } from '../slices/suppliersApiSlice';
import { toast } from 'react-toastify';

const OpeningStoreStocksEditScreen = () => {
  const { id: openingStoreStocksId } = useParams(); // Retrieve openingStoreStock ID
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [note, setNote] = useState('');
  const [openingStoreStockDate, setOpeningStoreStockDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: openingStoreStockData, isLoading: loadingOpeningStoreStock, error: openingStoreStockError } = useGetOpeningStoreStockDetailsQuery(openingStoreStocksId);
  const { data: suppliersData, isLoading: loadingSuppliers, error: suppliersError } = useGetSuppliersQuery({ keyword: supplierSearch });
  const [createOpeningStoreStock, { isLoading: savingOpeningStoreStock, }] = useCreateOpeningStoreStockMutation();

  // Load initial openingStoreStock data into the form
  useEffect(() => {
    if (openingStoreStockData) {
      setSelectedSupplier(openingStoreStockData.supplierDetails || null);
      setNote(openingStoreStockData.note || '');
      setOpeningStoreStockDate(openingStoreStockData.date ? openingStoreStockData.date.split('T')[0] : new Date().toISOString().split('T')[0]);
      dispatch(clearCartItems());
      openingStoreStockData.openingStoreStockItems.forEach((item) => {
        dispatch(
          addToCart({
            ...item,
            product: item.product, // Ensure product ID is preserved
            qty: item.qty,
          })
        );
      });
    }
  }, [openingStoreStockData, dispatch]);

  // Filter suppliers based on search input
  useEffect(() => {
    if (suppliersData?.suppliers) {
      const filtered = suppliersData.suppliers.filter((supplier) =>
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [supplierSearch, suppliersData]);

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setFilteredSuppliers([]);
  };

  const increaseQtyHandler = (item) => {
    dispatch(
      addToCart({
        ...item,
        qty: item.qty + 1,
      })
    );
  };

  const decreaseQtyHandler = (item) => {
    if (item.qty > 1) {
      dispatch(
        addToCart({
          ...item,
          qty: item.qty - 1,
        })
      );
    } else {
      toast.error('Quantity cannot be less than 1');
    }
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
  };

  const placeOpeningStoreStockHandler = async () => {
    try {
      if (!selectedSupplier) {
        toast.error('Please select a supplier');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      // Construct openingStoreStocksData payload for submission
      const openingStoreStocksData = {
        openingStoreStockItems: cartItems.map((item) => ({
          product: item.product, // Include product ID
          name: item.name,
          qty: item.qty,
          price: item.price,
          images: item.images[0], // Include first image
        })),
        supplierDetails: selectedSupplier._id,
        note,
        date: openingStoreStockDate,
      };

      // Submit as a new openingStoreStock record
      const response = await createOpeningStoreStock(openingStoreStocksData).unwrap();
      console.log('Response:', response); // Debug backend response
      dispatch(clearCartItems());
      toast.success('OpeningStoreStock successfully recorded');
      navigate('/house/HomeScreen'); // Redirect after success
    } catch (err) {
      console.error('Error:', err); // Debug error
      toast.error(err?.data?.message || 'Failed to record openingStoreStocks');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <h1>Edit OpeningMiniStock</h1>
        {loadingOpeningStoreStock ? (
          <Loader />
        ) : openingStoreStockError ? (
          <Message variant="danger">{openingStoreStockError?.data?.message || 'Failed to load openingStoreStock data'}</Message>
        ) : (
          <>
            {cartItems.length === 0 ? (
              <Message>
                Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
              </Message>
            ) : (
              <ListGroup variant="flush">
                {cartItems.map((item) => (
                  <ListGroup.Item key={item._id}>
                    <Row>
                      <Col md={2}>
                        {item.images?.length > 0 && (
                          <img
                            src={item.images[0]}
                            alt="Product Thumbnail"
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                      </Col>
                      <Col>{item.name}</Col>
                      <Col md={2}>${item.price.toFixed(2)}</Col>
                      <Col md={3}>
                        <div className="d-flex align-items-center">
                          <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                            <FaMinus />
                          </Button>
                          <span className="mx-2">{item.qty}</span>
                          <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                            <FaPlus />
                          </Button>
                        </div>
                      </Col>
                      <Col md={2}>${(item.qty * item.price).toFixed(2)}</Col>
                      <Col md={1}>
                        <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Col>
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Supplier</h2>
              {loadingSuppliers ? (
                <Loader />
              ) : suppliersError ? (
                <Message variant="danger">{suppliersError?.data?.message || 'Failed to load suppliers'}</Message>
              ) : (
                <>
                  <Form.Group controlId="supplierSearch">
                    <Form.Label>Search Supplier</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type supplier name"
                      value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                    />
                  </Form.Group>
                  {filteredSuppliers.length > 0 && (
                    <ListGroup>
                      {filteredSuppliers.map((supplier) => (
                        <ListGroup.Item
                          key={supplier._id}
                          action
                          onClick={() => handleSupplierSelect(supplier)}
                        >
                          {supplier.name} ({supplier.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {selectedSupplier && (
                    <div className="mt-3">
                      <strong>Selected Supplier:</strong>
                      <p>
                        {selectedSupplier.name} ({selectedSupplier.company})<br />
                        Phone: {selectedSupplier.telephone}<br />
                        Address: {selectedSupplier.address}<br />
                        SERIAL: {selectedSupplier.serialNumber}
                      </p>
                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group controlId="openingStoreStockDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={openingStoreStockDate}
                          onChange={(e) => setOpeningStoreStockDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>
            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={savingOpeningStoreStock || cartItems.length === 0 || !selectedSupplier}
                onClick={placeOpeningStoreStockHandler}
              >
                {savingOpeningStoreStock ? 'Processing...' : 'Submit OpeningStoreStock'}
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default OpeningStoreStocksEditScreen;
